// Space Designer (Phase 36) — CRM-backed shared custom-SVG library.
// Replaces SDS's Supabase `use-svg-cloud` hook. All assets are space-shared.
// Keeps the same surface DrawComponent expects (useAuthSession + useCreateCloudSvg).
import { useQueryClient } from "@tanstack/react-query";
import {
  useListSpaceSvgAssets,
  getListSpaceSvgAssetsQueryKey,
  useCreateSpaceSvgAsset,
  useUpdateSpaceSvgAsset,
  useDeleteSpaceSvgAsset,
} from "@workspace/api-client-react";
import { useAuth } from "@/context/auth-context";

/** SDS shape compatibility — the canvas is always behind CRM auth, so a session
 *  is always present. Kept so DrawComponent's save-local-vs-cloud branch works. */
export function useAuthSession() {
  const { user } = useAuth();
  return {
    session: user ? { userId: String(user.id), email: null as string | null } : { userId: "me", email: null },
    ready: true,
  };
}

/** Shared list of custom SVG assets for the current space. */
export function useSpaceSvgAssets() {
  return useListSpaceSvgAssets({ query: { queryKey: getListSpaceSvgAssetsQueryKey() } });
}

/**
 * SDS-compatible create hook. DrawComponent calls
 *   create.mutateAsync({ name, markup, width, height, isShared })
 * We map that to the CRM body { name, markup, viewBox, width, height }.
 */
export function useCreateCloudSvg() {
  const qc = useQueryClient();
  const mut = useCreateSpaceSvgAsset({
    mutation: {
      onSuccess: () => qc.invalidateQueries({ queryKey: getListSpaceSvgAssetsQueryKey() }),
    },
  });
  return {
    isPending: mut.isPending,
    mutateAsync: (input: { name: string; markup: string; width: number; height: number; isShared?: boolean }) =>
      mut.mutateAsync({
        data: {
          name: input.name,
          markup: input.markup,
          viewBox: `0 0 ${input.width} ${input.height}`,
          width: input.width,
          height: input.height,
        },
      }),
  };
}

export function useRenameSpaceSvg() {
  const qc = useQueryClient();
  return useUpdateSpaceSvgAsset({
    mutation: { onSuccess: () => qc.invalidateQueries({ queryKey: getListSpaceSvgAssetsQueryKey() }) },
  });
}

export function useDeleteSpaceSvg() {
  const qc = useQueryClient();
  return useDeleteSpaceSvgAsset({
    mutation: { onSuccess: () => qc.invalidateQueries({ queryKey: getListSpaceSvgAssetsQueryKey() }) },
  });
}
